/**
 * AssigneePicker — small popover that lets the user pick a workspace member
 * to assign a task to. Uses PersonChip for the current value and a list of
 * available candidates. Click outside / Escape closes the popover.
 *
 * Candidates are expected to be the workspace members of the task's
 * workspace — every workspace member is a valid assignee, and we don't yet
 * support restricting assignment to a project subset.
 */

import { useEffect, useRef, useState } from 'react'
import { useProfiles, pickDisplay, type PublicProfile } from '@/features/profiles/profiles'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/user/UserAvatar'

export interface AssigneePickerProps {
  value: string | null | undefined
  candidates: Array<{ id: string }>
  onChange: (userId: string | null) => void
  /** Disabled state — visible but not interactive. */
  disabled?: boolean
  /** Self user id; shown with a "(you)" suffix. */
  currentUserId?: string | null
}

export function AssigneePicker({ value, candidates, onChange, disabled, currentUserId }: AssigneePickerProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const ids = candidates.map((c) => c.id)
  const { profiles } = useProfiles(ids)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (e.target instanceof Node && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!value) {
    return (
      <div ref={wrapRef} className="relative inline-block">
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded border border-dashed border-surface-300 px-2 py-0.5 text-xs text-surface-500 hover:border-brand-500 hover:text-brand-600 dark:border-surface-700"
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-200 text-[9px] dark:bg-surface-700">+</span>
          Assign…
        </button>
        {open && <CandidateList candidates={candidates} profiles={profiles} onPick={(id) => { onChange(id); setOpen(false) }} currentUserId={currentUserId} />}
      </div>
    )
  }

  const profile: PublicProfile | undefined = profiles[value]
  const display = pickDisplay(value, profile)
  const isSelf = value === currentUserId

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded px-1.5 py-0.5 text-xs hover:bg-surface-100 dark:hover:bg-surface-800"
        title={display.email ?? display.displayName}
      >
        <UserAvatar name={display.displayName} email={display.email} sources={display.avatarSources} className="h-5 w-5 text-[9px]" pixelSize={40} decorative />
        <span>{display.displayName}</span>
        {isSelf && <span className="text-[9px] text-surface-500">(you)</span>}
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-64 rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-800">
          <CandidateList
            candidates={candidates}
            profiles={profiles}
            highlightId={value}
            onPick={(id) => {
              onChange(id)
              setOpen(false)
            }}
            onClear={() => {
              onChange(null)
              setOpen(false)
            }}
            currentUserId={currentUserId}
          />
        </div>
      )}
    </div>
  )
}

function CandidateList({
  candidates,
  profiles,
  onPick,
  onClear,
  highlightId,
  currentUserId,
}: {
  candidates: Array<{ id: string }>
  profiles: Record<string, PublicProfile>
  onPick: (id: string) => void
  onClear?: () => void
  highlightId?: string | null
  currentUserId?: string | null
}) {
  return (
    <>
      <div className="max-h-60 overflow-auto">
        {candidates.length === 0 && (
          <div className="px-3 py-2 text-xs text-surface-500">No eligible assignees.</div>
        )}
        {candidates.map((c) => {
          const p = profiles[c.id]
          const d = pickDisplay(c.id, p)
          const isSelf = c.id === currentUserId
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c.id)}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 dark:hover:bg-surface-700/60',
                highlightId === c.id && 'font-semibold',
              )}
            >
              <UserAvatar name={d.displayName} email={d.email} sources={d.avatarSources} className="h-5 w-5 text-[9px]" pixelSize={40} decorative />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{d.displayName}{isSelf && <span className="ml-1 text-[9px] text-surface-500">(you)</span>}</span>
                {d.email && <span className="block truncate text-[10px] text-surface-500">{d.email}</span>}
              </span>
            </button>
          )
        })}
      </div>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="block w-full border-t border-surface-200 px-3 py-1.5 text-left text-[11px] text-red-600 hover:bg-red-50 dark:border-surface-700 dark:text-red-300 dark:hover:bg-red-900/30"
        >
          Unassign
        </button>
      )}
    </>
  )
}
